// Admin subscription products.
//
// Lists every community that has a subscription product. Operator
// actions: pause new sign-ups, resume, force-cancel-all-and-pause
// (involuntary-cancellation flow with pro-rated refunds).
import { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Input, Label, Select, Textarea } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { Spinner } from "../../components/ui/Spinner";
import { api } from "../../lib/api";

interface ProductRow {
  id: string;
  community_id: string;
  is_enabled: boolean;
  monthly_price_usd: number;
  annual_price_usd: number | null;
  moderator_pool_pct: number;
  paused_at: string | null;
  paused_reason: string | null;
  updated_at: string;
  communities: {
    id: string;
    slug: string;
    name: string;
    created_by: string;
  } | null;
  active_member_count: number;
}

interface ListResponse {
  products: ProductRow[];
  total: number;
}

function formatUsd(n: number | null): string {
  if (n == null) return "—";
  return `$${Number(n).toFixed(2)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

type ActionTarget =
  | { kind: "pause"; product: ProductRow }
  | { kind: "force_cancel"; product: ProductRow }
  | null;

export default function AdminSubscriptionProductsPage() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<
    "all" | "enabled" | "paused" | "disabled"
  >("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [target, setTarget] = useState<ActionTarget>(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (debouncedSearch) params.set("search", debouncedSearch);
      params.set("limit", "100");
      const res = await api<ListResponse>(
        `/admin/subscription-products?${params.toString()}`,
      );
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  async function resumeProduct(product: ProductRow) {
    try {
      await api(`/admin/subscription-products/${product.id}/resume`, {
        method: "POST",
      });
      await fetchList();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Resume failed");
    }
  }

  async function submitAction() {
    if (!target) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const action = target.kind === "pause" ? "pause" : "force-cancel";
      await api(`/admin/subscription-products/${target.product.id}/${action}`, {
        method: "POST",
        body: { reason: reason.trim() },
      });
      setTarget(null);
      setReason("");
      await fetchList();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setSubmitting(false);
    }
  }

  const rows = data?.products ?? [];

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.h1}>Subscription Products</h1>
        <CardSubtle>
          Communities with active subscription products. Pause stops new
          sign-ups while existing subscribers keep billing. Force-cancel
          cancels every active subscription immediately and issues
          pro-rated refunds.
        </CardSubtle>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div style={styles.filterRow}>
          <div style={{ flex: 1 }}>
            <Label>Status</Label>
            <Select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(
                  e.target.value as typeof statusFilter,
                )
              }
            >
              <option value="all">All</option>
              <option value="enabled">Enabled</option>
              <option value="paused">Paused</option>
              <option value="disabled">Disabled</option>
            </Select>
          </div>
          <div style={{ flex: 2 }}>
            <Label>Search community</Label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="slug or name"
            />
          </div>
        </div>
      </Card>

      {loading ? (
        <Card>
          <div style={styles.loadingWrap}>
            <Spinner />
          </div>
        </Card>
      ) : error ? (
        <Card>
          <div style={styles.errorBar}>{error}</div>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardTitle>No products</CardTitle>
          <CardSubtle>Nothing matches the current filters.</CardSubtle>
        </Card>
      ) : (
        <Card padding={0}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHead}>
                <Th>Community</Th>
                <Th>Monthly</Th>
                <Th>Annual</Th>
                <Th>Subscribers</Th>
                <Th>Status</Th>
                <Th>Updated</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isPaused = !!row.paused_at;
                const statusText = !row.is_enabled
                  ? "disabled"
                  : isPaused
                    ? "paused"
                    : "enabled";
                return (
                  <tr key={row.id} style={styles.tableRow}>
                    <Td>
                      <strong>r/{row.communities?.slug ?? "?"}</strong>
                      <CardSubtle style={{ fontSize: 12 }}>
                        {row.communities?.name}
                      </CardSubtle>
                    </Td>
                    <Td>{formatUsd(row.monthly_price_usd)}</Td>
                    <Td>{formatUsd(row.annual_price_usd)}</Td>
                    <Td>{row.active_member_count}</Td>
                    <Td>
                      <span style={{ color: statusColor(statusText) }}>
                        {statusText}
                      </span>
                      {row.paused_reason && (
                        <CardSubtle
                          style={{ fontSize: 12, fontStyle: "italic" }}
                        >
                          {row.paused_reason}
                        </CardSubtle>
                      )}
                    </Td>
                    <Td>
                      <CardSubtle style={{ fontSize: 12 }}>
                        {formatDate(row.updated_at)}
                      </CardSubtle>
                    </Td>
                    <Td>
                      <div style={{ display: "flex", gap: 6 }}>
                        {isPaused ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => resumeProduct(row)}
                          >
                            Resume
                          </Button>
                        ) : (
                          row.is_enabled && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() =>
                                setTarget({ kind: "pause", product: row })
                              }
                            >
                              Pause
                            </Button>
                          )
                        )}
                        {row.active_member_count > 0 && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() =>
                              setTarget({ kind: "force_cancel", product: row })
                            }
                          >
                            Force cancel
                          </Button>
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <Modal
        open={target !== null}
        onClose={() => !submitting && setTarget(null)}
        title={
          target?.kind === "pause"
            ? `Pause subscriptions for r/${target.product.communities?.slug ?? "?"}`
            : target?.kind === "force_cancel"
              ? `Force cancel all subscriptions for r/${target.product.communities?.slug ?? "?"}`
              : ""
        }
      >
        <CardSubtle style={{ marginBottom: 12 }}>
          {target?.kind === "pause"
            ? "Stops new sign-ups. Existing subscribers continue billing until they cancel."
            : `Cancels every active subscription (${target?.product.active_member_count ?? 0}) immediately and issues pro-rated refunds of the unused portion of the current billing period. Pauses the product. Cannot be undone.`}
        </CardSubtle>
        <Label>Reason</Label>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder={
            target?.kind === "pause"
              ? "Why are you pausing this product?"
              : "Why are you force-cancelling? (e.g. owner banned)"
          }
        />
        {actionError && (
          <div style={{ ...styles.errorBar, marginTop: 12 }}>{actionError}</div>
        )}
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <Button
            variant="secondary"
            onClick={() => setTarget(null)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant={target?.kind === "force_cancel" ? "danger" : "primary"}
            onClick={submitAction}
            disabled={reason.trim().length < 3 || submitting}
          >
            {submitting
              ? "Working…"
              : target?.kind === "pause"
                ? "Pause subscriptions"
                : "Force cancel all"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function statusColor(s: string): string {
  switch (s) {
    case "enabled":
      return "var(--upvote, #22C55E)";
    case "paused":
      return "var(--accent)";
    case "disabled":
      return "var(--text-secondary)";
    default:
      return "var(--text-primary)";
  }
}

function Th({ children }: { children: React.ReactNode }) {
  return <th style={styles.th}>{children}</th>;
}
function Td({ children }: { children: React.ReactNode }) {
  return <td style={styles.td}>{children}</td>;
}

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: 16 },
  h1: { margin: 0, fontSize: 24, fontWeight: 700, color: "var(--text-primary)" },
  filterRow: { display: "flex", gap: 12 },
  loadingWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 0",
  },
  errorBar: {
    background: "rgba(239,68,68,0.1)",
    color: "var(--danger, #ef4444)",
    padding: 12,
    borderRadius: "var(--radius-sm)",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  tableHead: { background: "rgba(255,255,255,0.02)", textAlign: "left" },
  tableRow: { borderTop: "1px solid var(--border)" },
  th: {
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--text-secondary)",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  td: { padding: 12, verticalAlign: "top", color: "var(--text-primary)" },
};
